import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { IS_LOCAL_EDITION } from "../lib/edition";
import { dteApi, humanizeError } from "../lib/api";

export default function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  const [licenseLoading, setLicenseLoading] = useState(IS_LOCAL_EDITION);
  const [licenseActive, setLicenseActive] = useState(!IS_LOCAL_EDITION);
  const [machineId, setMachineId] = useState("");
  const [serial, setSerial] = useState("");
  const [activating, setActivating] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!IS_LOCAL_EDITION) return;
    let mounted = true;
    (async () => {
      setLicenseLoading(true);
      setErr("");
      try {
        const lic = await dteApi.localLicense();
        if (!mounted) return;
        setLicenseActive(!!lic?.active);
        setMachineId(lic?.machineId || "");
      } catch (e) {
        if (!mounted) return;
        setLicenseActive(false);
        setErr(humanizeError(e, "No se pudo verificar licencia local."));
      } finally {
        if (mounted) setLicenseLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const activateLicense = async () => {
    if (!serial.trim()) return;
    setActivating(true);
    setErr("");
    try {
      const lic = await dteApi.localActivateLicense(serial.trim());
      setLicenseActive(!!lic?.active);
      setMachineId(lic?.machineId || machineId);
      setSerial("");
    } catch (e) {
      setErr(humanizeError(e, "No se pudo activar la licencia."));
    } finally {
      setActivating(false);
    }
  };

  const copyMachineId = async () => {
    if (!machineId) return;
    try {
      await navigator.clipboard.writeText(machineId);
    } catch {
      // noop
    }
  };

  if (IS_LOCAL_EDITION && licenseLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen text-sm text-muted-foreground" data-testid="license-loading">
        Verificando licencia local...
      </div>
    );
  }
  if (IS_LOCAL_EDITION && !licenseActive) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="license-gate">
        <div className="w-full max-w-2xl rounded-2xl border bg-card p-6 sm:p-8 shadow-sm">
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">Activar DTE Flow Local</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Esta instalación está bloqueada hasta ingresar un serial válido.
          </p>
          <div className="mt-6 space-y-3">
            <label className="block text-xs uppercase tracking-wide text-muted-foreground">ID de equipo</label>
            <div className="flex gap-2">
              <input
                value={machineId}
                readOnly
                className="flex-1 rounded-md border bg-muted/40 px-3 py-2 text-sm font-mono"
              />
              <button
                type="button"
                onClick={copyMachineId}
                className="rounded-md border px-3 py-2 text-sm hover:bg-muted"
              >
                Copiar
              </button>
            </div>
            <label className="block text-xs uppercase tracking-wide text-muted-foreground mt-4">Serial</label>
            <input
              value={serial}
              onChange={(e) => setSerial(e.target.value)}
              placeholder="DTEF-FULL-XXXXXXXXXXXXXXX-XXXXXXXXXX"
              className="w-full rounded-md border px-3 py-2 text-sm font-mono"
            />
            {err && <p className="text-xs text-red-500">{err}</p>}
            <button
              type="button"
              disabled={activating || !serial.trim()}
              onClick={activateLicense}
              className="w-full rounded-md bg-slate-900 text-white py-2.5 text-sm font-medium disabled:opacity-60"
            >
              {activating ? "Activando..." : "Activar licencia"}
            </button>
          </div>
        </div>
      </div>
    );
  }
  if (loading) return (
    <div className="flex items-center justify-center min-h-screen text-sm text-muted-foreground" data-testid="auth-loading">
      Verificando sesión...
    </div>
  );
  if (!user) return <Navigate to="/login" state={{ from: loc }} replace />;
  return children;
}
